import axios from 'axios';
import CommonButton from 'components/Buttons/CommonButton';
import Modal from 'components/Modal';
import SimpleTable from 'components/SimpleTable/SimpleTable';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import services from 'services';
import selectors from 'state/selectors';
import styled from 'styled-components/macro';
import IntlHelper from 'utils/helpers/intl.helper';
import { colorPalette } from 'utils/styleConstants';

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  min-width: 250px;
  gap: 8px;
  margin-bottom: 30px;
`;
const TableWrapper = styled.div`
  margin-top: 20px;
  display: grid;
  justify-content: center;
`;
const Error = styled.p`
  color: ${({ theme }) => theme.colorPalette.red};
  font-size: ${({ theme }) => theme.fontSizePalette.medium};
  margin-top: 20px;
`;
const Text = styled.h1<{ textSize?: string }>`
  font-size: ${({ theme, textSize }) => textSize ?? theme.fontSizePalette.small};
  color: ${({ theme }) => theme.colorPalette.grey.grey6};
`;
const Strong = styled.strong`
  color: ${({ theme }) => theme.colorPalette.black};
  font-weight: ${({ theme }) => theme.fontWeightPalette.bold};
`;
const Label = styled.div`
  text-align: left;
  margin-bottom: 25px;
`;

const ReverseRepost = () => {
  const t = IntlHelper.useTranslator();
  const [isLoading, setIsLoading] = React.useState(false);
  const reverseRepost = services.reverseRepost;
  const props: {
    idProtocol: number;
    idUser: number;
    idProgram: number;
    automaticDistribute: boolean;
  } = useSelector(selectors.modal.getModalProps);
  const [data, setData] = React.useState<{ title: string; info: string | number }[] | string>([]);
  const [documentIds, setDocumentIds] = useState<{ value: any; message: string }[]>([]);
  type OperationType = 'reverse' | 'repost' | 'reverse-repost' | '';
  const [operation, setOperation] = useState<OperationType>('');

  const setTableValues = (data: { [key: string]: string | number }) => {
    setData([
      {
        title: t('documentType'),
        info: data?.documentType,
      },
      {
        title: t('subject'),
        info: data?.subject,
      },
      {
        title: t('supplierCode'),
        info: data?.supplierCode,
      },
      {
        title: t('vatNumber'),
        info: data?.vatNumber,
      },
      {
        title: t('companyName'),
        info: data?.companyName,
      },
    ]);
  };
  const onClick = async (operation: OperationType) => {
    try {
      setIsLoading(true);
      const promise = await reverseRepost({
        automaticDistribute: false,
        documentAction: operation,
        protocol: props.idProtocol,
        idProgram: props.idProgram,
      });
      setOperation(operation);
      if (operation === 'reverse') {
        setTableValues(promise.data.reverseDocument);
        setDocumentIds([{ value: promise.data.reverseDocument.protIn, message: t('reverse-message') }]);
        return;
      }
      if (operation === 'repost') {
        setTableValues(promise.data.repostDocument);
        setDocumentIds([{ value: promise.data.repostDocument.protIn, message: t('repost-message') }]);
        return;
      }
      setTableValues(promise.data.repostDocument);
      setDocumentIds([
        { value: promise.data.repostDocument.protIn, message: t('repost-message') },
        { value: promise.data.reverseDocument.protIn, message: t('reverse-message') },
      ]);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setData(error.response?.data.message);
      }
    }
  };

  return (
    <>
      <Modal.Header title={t('TeverseRepost')} />
      <Label>
        {t('protocol')}: {props.idProtocol}
      </Label>
      <Modal.Content>
        <ButtonGroup>
          <CommonButton
            id="reverse-btn"
            action={async () => {
              await onClick('reverse');
              setIsLoading(false);
            }}
            scope="primary"
            value={t('reverse')}
            disabled={isLoading || operation !== ''}
            disabledColor={operation === 'reverse' ? colorPalette.turquoise.light : undefined}
          />
          <CommonButton
            id="repost-btn"
            action={async () => {
              await onClick('repost');
              setIsLoading(false);
            }}
            scope="primary"
            value={t('repost')}
            disabled={isLoading || operation !== ''}
            disabledColor={operation === 'repost' ? colorPalette.turquoise.light : undefined}
          />
          <CommonButton
            id="reverse-repost-btn"
            action={async () => {
              await onClick('reverse-repost');
              setIsLoading(false);
            }}
            scope="primary"
            value={t('reverse-repost')}
            disabled={isLoading || operation !== ''}
            disabledColor={operation === 'reverse-repost' ? colorPalette.turquoise.light : undefined}
          />
        </ButtonGroup>
        {typeof data === 'object' && data.length > 0 ? (
          <>
            <Text textSize={'22px'}>{t('DocumentApClerk')}</Text>
            {documentIds.map((item, index) => (
              <Text key={index} data-walkthrough="new-document-barcode">
                {item.message}{' '}
                <Strong>
                  {t('ID Lucy:')} {item.value}
                </Strong>
              </Text>
            ))}
            <TableWrapper>
              <SimpleTable data={data} title={'document-info'} />
            </TableWrapper>
          </>
        ) : null}
        {typeof data === 'string' && <Error>{data}</Error>}
      </Modal.Content>
      <Modal.Footer
        withConfirmButton={false}
        withCloseButton
        closeButtonText={operation !== '' ? t('close') : t('cancel')}
      />
    </>
  );
};

export default ReverseRepost;
